// Trace reading — the on-demand referee over ONE open trace.
//
// The deterministic drafters (the X-ray extractors) already claim this
// trace's calls in the semantic feed; this agent judges that reading against
// the raw bytes, call by call: each drafted claim comes back accepted or
// rejected, and work the drafters missed comes back as new items that MUST
// carry a verbatim quote from the call's own bytes — machine-verified here,
// exactly like the review's and the hand-off scan's citations. The judge is
// a referee, never a generator: an item without a surviving quote is
// discarded (and counted), so nothing model-authored reaches the timeline
// unanchored.
//
// Epistemics: verified additions render as their own tier ("verified ·
// model"), never join the feed's claim/coverage accounting, never feed
// provenance. The auditor's path to ground truth is the same source chips
// every mark carries.
//
// The verdict LEDGER is the part that outlives one trace: verdicts persist
// in localStorage keyed by (call content hash, claims hash, model), so
// re-opening a trace — or opening the next cumulative request of the same
// session, which re-carries the same calls — reuses verdicts instead of
// re-spending. The same ledger is the local gap-mining corpus:
// readingGapShapes() ranks the shapes whose calls keep producing verified
// additions, which is the drafter backlog as observed from this operator's
// own reading.

import { buildSemanticFeed } from "./traceSemanticFeed.js";
import { readToolCall } from "./traceToolCall.js";
import { isCodeModeCell, extractCellCalls } from "./traceCodeModeCells.js";
import { normalizeForMatch, windowBudgetChars } from "./traceEvaluation.js";
import { runSubAgent, runWithConcurrency } from "./subAgent.js";
import { createEntryScanner } from "./traceReadingStream.js";
import { lookupCatalogModel } from "./modelCatalog.js";
import { parseForgePath } from "./traceForgeOps.js";
import { buildTraceLanes } from "./traceLanes.js";

const READ_CONCURRENCY = 3;
const SOURCE_CAP = 6000;
const RESULT_CAP = 4000;
const LEDGER_KEY = "origin.trace-reading.ledger.v1";
const LEDGER_MAX = 400;
// Joins the ledger key: verdicts answer a specific ASKING, and a judge-prompt
// change (e.g. tightening subject discipline) must re-judge rather than reuse
// answers to the old question. Bump on any semantic prompt change.
const PROMPT_VERSION = 2;

// ── result cache (session-scoped, same discipline as the review's) ──────────
const RESULT_CACHE = new Map();
const RESULT_CACHE_MAX = 20;

// The session a trace's reading is kept under: the one its turns are of, so
// every place that reads a session (its lanes view, opened on any of its
// requests' ids, or a card of it on a board) reads one reading of it.
export function readingSessionOf(trace) {
  return String(trace?.sessionId || trace?.id || "");
}
export function readingCacheKey({ trace, model, claimsHash }) {
  return `${readingSessionOf(trace)}:${model || ""}:${claimsHash || ""}`;
}
// The fingerprint of the claims a reading judged, which its key carries: a
// drafter upgrade changes the claims, and reusing a verdict on claims that no
// longer exist would judge the wrong reading.
export function readingClaimsHash(drafted) {
  return drafted?.calls?.length ? hashString(drafted.calls.map((c) => `${c.contentHash}:${c.claimsHash}`).join("|")) : "";
}
export function readReadingCache(key) {
  return RESULT_CACHE.get(key) || null;
}
// Who reads one trace's reading (its lanes view, a dock reading a cell of it,
// the workspace home's Reading): told when a run lands, wherever it ran. A
// board is told which session a run landed for, and looks one up only for
// the cards whose session has one, since finding a card's means drafting its
// calls.
const RESULT_LISTENERS = new Map();
const SESSION_LISTENERS = new Set();
const KEY_SESSION = new Map();
function forgetReading(key) {
  RESULT_CACHE.delete(key);
  KEY_SESSION.delete(key);
}
export function writeReadingCache(key, report, session = null) {
  if (RESULT_CACHE.size >= RESULT_CACHE_MAX) {
    const oldest = RESULT_CACHE.keys().next().value;
    if (oldest !== undefined) forgetReading(oldest);
  }
  RESULT_CACHE.set(key, report);
  if (session) KEY_SESSION.set(key, String(session));
  for (const fn of [...(RESULT_LISTENERS.get(key) || [])]) fn(report);
  if (session) for (const fn of [...SESSION_LISTENERS]) fn(String(session));
}
export function sessionHasReading(session) {
  const s = String(session || "");
  if (!s) return false;
  for (const kept of KEY_SESSION.values()) if (kept === s) return true;
  return false;
}
export function subscribeReadingSessions(fn) {
  SESSION_LISTENERS.add(fn);
  return () => { SESSION_LISTENERS.delete(fn); };
}
export function subscribeReadingCache(key, fn) {
  if (!key) return () => {};
  if (!RESULT_LISTENERS.has(key)) RESULT_LISTENERS.set(key, new Set());
  RESULT_LISTENERS.get(key).add(fn);
  return () => {
    const set = RESULT_LISTENERS.get(key);
    set?.delete(fn);
    if (set && !set.size) RESULT_LISTENERS.delete(key);
  };
}

// djb2 — the sibling libs' stable hash.
export function hashString(s) {
  let h = 5381;
  const t = String(s || "");
  for (let i = 0; i < t.length; i++) h = ((h << 5) + h + t.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

const clip = (s, n) => {
  const t = String(s ?? "");
  return t.length > n ? t.slice(0, n) : t;
};

// ── drafted claims, per call ─────────────────────────────────────────────────
// The feed's items ARE the drafters' reading — including runtime lenses and
// the code fallback — so claims come from one buildSemanticFeed pass, grouped
// by the call (base block id) they claim. Residual calls join with an empty
// claim list: "the drafters said nothing" is exactly what the judge should
// hear about them.

function claimLine(domain, op) {
  switch (domain) {
    case "files":
      return {
        kind: op.kind,
        subject: op.path,
        detail: op.kind === "read"
          ? `${op.readLines || 0} lines observed`
          : `+${op.plus || 0} −${op.minus || 0}${op.derived ? ` · derived (${op.basis})` : ""}${op.hunks?.length ? ` · first hunk: ${clip(op.hunks[0].old, 60)} → ${clip(op.hunks[0].new, 60)}` : ""}`,
      };
    case "forge":
      return {
        kind: op.create ? "create" : op.kind,
        subject: [op.repo, op.res, op.num ? `#${op.num}` : null].filter(Boolean).join(" "),
        detail: [op.action, op.endpoint, op.title].filter(Boolean).join(" · "),
      };
    case "query":
    case "grafana":
      return { kind: op.kind || "query", subject: op.target || op.instance || op.engine, detail: clip(op.query || "", 160) };
    case "research":
      return { kind: op.kind, subject: op.kind === "search" ? op.query : op.url, detail: null };
    case "recall":
      return { kind: op.kind, subject: op.kind === "docs" ? (op.entities || []).join(",") : op.request, detail: null };
    case "linear":
      return { kind: op.create ? "create" : op.kind, subject: op.issue || "issues", detail: clip(op.title || "", 100) };
    case "code":
      return {
        kind: op.kind,
        subject: op.kind === "cell" ? clip((op.command || op.code || "").split("\n")[0], 140) : String(op.cellId || "cell"),
        detail: op.kind === "cell" && op.tools?.length ? `calls ${op.tools.join(",")}` : null,
      };
    case "artifact":
      return { kind: "publish", subject: op.title || op.path, detail: null };
    case "other":
      return null; // residual — no claim to defend
    default: {
      const subject = op.path || op.url || op.target || op.issue || op.res || op.name || null;
      return subject ? { kind: op.kind || "op", subject, detail: null } : null;
    }
  }
}

/**
 * How the judge's window divides between the readable rendering and the raw
 * arguments, and which half lost characters to the cap.
 *
 * Pure and cap-agnostic on purpose. With the real constants the readable half
 * is capped at CALL_CAP (1600) far below the window's SOURCE_CAP (6000), so
 * `headCut` cannot rise above zero -- the window can only ever reach the raw
 * tail. The arithmetic still has to be right for the case where it can, and
 * taking cap sizes as arguments is what lets that case be exercised at all.
 *
 * The separator only exists when BOTH halves were joined: a call whose
 * readable rendering is empty (an input object with no keys renders to "",
 * while its raw form is still "{}") is dropped from the join, and skipping a
 * separator that was never written eats the opening brace.
 */
export function splitJudgeSource({ head = "", json = "", source = "", fullLength = 0 } = {}) {
  const capCut = Math.max(0, fullLength - source.length);
  const jsonCut = json ? Math.min(capCut, json.length) : 0;
  const headLen = Math.min(head.length, source.length);
  const sepLen = head && json && source.length > headLen ? 1 : 0;
  return {
    callText: source.slice(0, headLen),
    callJson: source.slice(headLen + sepLen),
    headCut: capCut - jsonCut,
    jsonCut,
  };
}

/**
 * One judgeable unit per tool call: the call's bytes (through the shared
 * cell-aware reader), the drafters' claims about it, and identity hashes.
 */
export function draftReadingCalls(turns) {
  const feed = buildSemanticFeed(Array.isArray(turns) ? turns : []);
  const byBase = new Map();
  for (const row of feed.rows) {
    for (const item of row.items || []) {
      if (item.domain === "prompt" || item.domain === "reply" || item.turnIndex == null) continue;
      const base = String(item.op?.key ?? item.id.slice(item.domain.length + 1)).split("#")[0];
      let entry = byBase.get(base);
      if (!entry) {
        entry = { id: base, turnIndex: item.turnIndex, blockIndex: item.blockIndex, claims: [] };
        byBase.set(base, entry);
      }
      if (item.domain !== "other") {
        const c = claimLine(item.domain, item.op || {});
        if (c?.subject) entry.claims.push({ domain: item.domain, ...c });
      }
    }
  }
  const calls = [];
  for (const entry of byBase.values()) {
    const read = readToolCall(turns, entry.turnIndex, entry.blockIndex);
    if (!read) continue;
    const block = turns[entry.turnIndex]?.blocks?.[entry.blockIndex];
    const head = read.call || "";
    const json = read.callJson && read.callJson !== read.call ? read.callJson : "";
    const fullSource = [head, json].filter(Boolean).join("\n");
    const fullResult = read.result || "";
    const source = clip(fullSource, SOURCE_CAP);
    const result = clip(fullResult, RESULT_CAP);
    // The judge is handed the call twice on purpose -- the readable rendering
    // first, then the raw arguments -- so a reader shown one undifferentiated
    // blob sees the same thing said twice and no reason why. Split for display
    // by SLICING the judge's own bytes, never by re-deriving the halves:
    // sliced, a part cannot show a character the cap already took.
    const split = splitJudgeSource({ head, json, source, fullLength: fullSource.length });
    const shape = isCodeModeCell(block)
      ? `cell:${[...new Set(extractCellCalls(block.input.input).map((c) => c.tool))].sort().join("+") || "pure-js"}`
      : `tool:${read.name}`;
    calls.push({
      ...entry,
      name: read.name,
      source,
      result,
      // The two halves of `source`, exactly as the judge received them, each
      // carrying the characters IT lost. Both clip stages are counted:
      // readToolCall clips first and reports how much, then the judge's window
      // clips again. Counting only the second reported "nothing was cut" on a
      // call the first had already truncated.
      callText: split.callText,
      callJson: split.callJson,
      callTextClipped: (read.callClipped || 0) + split.headCut,
      callJsonClipped: split.jsonCut,
      resultClipped: (read.resultClipped || 0) + Math.max(0, fullResult.length - result.length),
      shape,
      contentHash: hashString(`${source}\n${result}`),
      claimsHash: hashString(JSON.stringify(entry.claims)),
    });
  }
  calls.sort((a, b) => a.turnIndex - b.turnIndex || a.blockIndex - b.blockIndex);
  return { calls, coverage: feed.coverage };
}

/**
 * The previous read, applied as a drafter: its quote-verified additions join
 * each call's claim list (flagged `promoted`) so a clean-context re-run
 * judges them like any drafted claim — confirmed into the draft or refuted.
 * The judge card renders promoted claims indistinguishably from extractor
 * claims on purpose: a clean second pass must not know which claims came
 * from a model. Per-call claimsHash is recomputed; contentHash is untouched.
 */
export function mergePromotedClaims(drafted, prior) {
  const byId = new Map();
  const seen = new Set();
  const add = (id, m) => {
    const k = `${id}|${m.kind}|${m.subject}`;
    if (seen.has(k)) return;
    seen.add(k);
    const list = byId.get(id) || [];
    list.push(m);
    byId.set(id, list);
  };
  for (const call of prior?.calls || []) {
    for (const m of call.missing || []) add(call.id, m);
    // A promotion CONFIRMED by the last re-run lives in claims, not missing —
    // it must stay in the pool or consecutive re-runs silently drop it from
    // the draft.
    (call.claims || []).forEach((c, i) => {
      if (!c.promoted) return;
      if ((call.verdicts || []).some((v) => v.claim === i && v.verdict === "accept")) add(call.id, c);
    });
  }
  if (!byId.size) return drafted;
  const calls = (drafted?.calls || []).map((call) => {
    const missing = byId.get(call.id);
    if (!missing?.length) return call;
    const claims = [
      ...call.claims,
      ...missing.map((m) => ({
        domain: additionOp(m.kind, m.subject, m.quote).domain,
        kind: m.kind,
        subject: m.subject,
        // The anchoring quote rides along for lane materialization; the judge
        // card never renders it, so the clean context stays clean.
        quote: m.quote,
        promoted: true,
      })),
    ];
    return { ...call, claims, claimsHash: hashString(JSON.stringify(claims)) };
  });
  return { ...drafted, calls };
}

// ── the judge ────────────────────────────────────────────────────────────────

export const READING_REPORT_SCHEMA = {
  type: "object",
  properties: {
    calls: {
      type: "array",
      description: "One entry per CALL block you were given, in any order. Every call must appear.",
      items: {
        type: "object",
        properties: {
          id: { type: "string", description: "The call id EXACTLY as given." },
          verdicts: {
            type: "array",
            description: "One entry per numbered claim on this call.",
            items: {
              type: "object",
              properties: {
                claim: { type: "number", description: "The claim's number as given." },
                verdict: { type: "string", description: "accept | reject" },
                reason: { type: "string", description: "Only when rejecting: why, in <=15 words." },
              },
              required: ["claim", "verdict"],
            },
          },
          missing: {
            type: "array",
            description: "Work this call did that NO claim covers. Empty when the claims tell the story.",
            items: {
              type: "object",
              properties: {
                kind: { type: "string", description: "Short kebab-case kind (file-read, api-call, query, process-control, ...)." },
                subject: { type: "string", description: "The path/url/query/target the work addressed." },
                quote: { type: "string", description: "An EXACT substring (<=160 chars) copied verbatim from this call's SOURCE or RESULT proving it. Machine-verified; a paraphrase is rejected." },
              },
              required: ["kind", "subject", "quote"],
            },
          },
        },
        required: ["id"],
      },
    },
  },
  required: ["calls"],
};

export function readingSystemPrompt() {
  return [
    "You audit an automated reading of an AI agent's tool calls. For each CALL you get its SOURCE (the call as issued — for code cells, the inner command first, then the raw program), its RESULT, and numbered CLAIMS produced by deterministic extractors about the work it did.",
    "",
    "For each claim: \"accept\" when it correctly and specifically describes work the call did (right kind, right subject); \"reject\" when it is wrong, misattributed, or so vague it misleads — say why in a few words.",
    "",
    "Then list work the claims MISSED entirely: file reads/edits/writes, network or API calls, queries, searches, repo operations, process control. Each missed item needs a kind, the subject, and a quote copied EXACTLY from that call's SOURCE or RESULT. No quote, no item. Do not restate accepted claims. Do not speculate about intent.",
    "",
    "The subject must be a LITERAL machine target copied from the bytes — the exact file path, the full URL or API endpoint path (repos/owner/repo/pulls/123), the query text, the command — never a description of it. \"Forgejo PR 1091 metadata\" is a description; \"repos/Acme/widgets/pulls/1091\" is a subject. When no literal target exists, use the most specific literal token your quote contains.",
    "",
    "A call with no claims means the extractors said nothing about it — read it and list its work as missing items (with quotes), or return it with an empty list when it did no substantive work (a sleep, a poll, pure formatting).",
  ].join("\n");
}

function callCard(call) {
  return [
    `CALL ${call.id} (turn ${call.turnIndex})`,
    "CLAIMS:",
    ...(call.claims.length
      ? call.claims.map((c, i) => `${i}. [${c.domain}/${c.kind}] ${c.subject}${c.detail ? ` — ${c.detail}` : ""}`)
      : ["(none drafted)"]),
    "SOURCE:",
    "```",
    call.source || "(empty)",
    "```",
    "RESULT:",
    "```",
    call.result || "(none)",
    "```",
  ].join("\n");
}

/** Pack calls into byte-budgeted chunks, order preserved. */
export function packReadingChunks(calls, budget) {
  const chunks = [];
  let cur = [];
  let size = 0;
  for (const call of calls) {
    const card = callCard(call);
    if (cur.length && size + card.length > budget) {
      chunks.push(cur);
      cur = [];
      size = 0;
    }
    cur.push(call);
    size += card.length;
  }
  if (cur.length) chunks.push(cur);
  return chunks;
}

/** The verbatim-quote gate, against ONE call's own bytes. */
export function quoteVerifies(quote, call) {
  const needle = normalizeForMatch(String(quote || ""));
  if (!needle || needle.length < 3) return false;
  return normalizeForMatch(`${call.source}\n${call.result}`).includes(needle);
}

// ── the ledger ───────────────────────────────────────────────────────────────
// localStorage, one bucket, evict-oldest. An entry is one judged call:
// verdicts for its claims, the surviving missing items, and the shape key the
// gap miner clusters on. Keyed so a drafter upgrade (claims change) or a
// model change re-judges rather than reusing a stale verdict.

function ledgerEntryKey(call, model) {
  return `${call.contentHash}:${call.claimsHash}:${model || ""}:p${PROMPT_VERSION}`;
}

function readLedgerRaw() {
  try {
    const raw = window.localStorage?.getItem(LEDGER_KEY);
    const v = raw ? JSON.parse(raw) : null;
    return v && typeof v === "object" && !Array.isArray(v) ? v : {};
  } catch {
    return {};
  }
}

function writeLedgerRaw(ledger) {
  try {
    const keys = Object.keys(ledger);
    if (keys.length > LEDGER_MAX) {
      keys.sort((a, b) => (ledger[a].at || 0) - (ledger[b].at || 0));
      for (const k of keys.slice(0, keys.length - LEDGER_MAX)) delete ledger[k];
    }
    window.localStorage?.setItem(LEDGER_KEY, JSON.stringify(ledger));
  } catch {
    /* private mode / quota — the ledger is an accelerator, not a requirement */
  }
}

export function readLedgerEntry(call, model) {
  return readLedgerRaw()[ledgerEntryKey(call, model)] || null;
}

export function writeLedgerEntries(entries, model) {
  if (!entries.length) return;
  const ledger = readLedgerRaw();
  const now = Date.now();
  for (const { call, verdicts, missing } of entries) {
    ledger[ledgerEntryKey(call, model)] = { shape: call.shape, verdicts, missing, at: now };
  }
  writeLedgerRaw(ledger);
}

/**
 * The local gap corpus, mined: shapes ranked by how often reading them
 * produced verified additions the drafters missed. Each row carries example
 * kinds/subjects — the seed of a drafter brief.
 */
export function readingGapShapes(limit = 8) {
  const byShape = new Map();
  for (const entry of Object.values(readLedgerRaw())) {
    if (!entry?.shape) continue;
    const s = byShape.get(entry.shape) || { shape: entry.shape, judged: 0, missing: 0, rejected: 0, kinds: new Map(), subjects: [] };
    s.judged += 1;
    s.rejected += (entry.verdicts || []).filter((v) => v.verdict === "reject").length;
    for (const m of entry.missing || []) {
      s.missing += 1;
      s.kinds.set(m.kind, (s.kinds.get(m.kind) || 0) + 1);
      if (s.subjects.length < 4 && m.subject) s.subjects.push(m.subject);
    }
    byShape.set(entry.shape, s);
  }
  return [...byShape.values()]
    .filter((s) => s.missing || s.rejected)
    .sort((a, b) => b.missing + b.rejected - (a.missing + a.rejected))
    .slice(0, limit)
    .map((s) => ({ ...s, kinds: [...s.kinds.entries()].sort((a, b) => b[1] - a[1]).map(([k, n]) => `${k}×${n}`) }));
}

// ── finalize ─────────────────────────────────────────────────────────────────

export function finalizeReading(calls, judged) {
  const byId = new Map(judged.map((j) => [j.id, j]));
  const out = [];
  let accepted = 0;
  let rejected = 0;
  let verified = 0;
  let discarded = 0;
  let judgedCalls = 0;
  // Promoted claims (a previous read applied as a drafter) are accounted
  // apart from extractor claims: they must not inflate the drafted totals
  // the longitudinal delta compares, and their verdicts mean something
  // different — a prior reading confirmed into the draft, or refuted.
  let promotedTotal = 0;
  let promotedConfirmed = 0;
  let promotedRefuted = 0;
  for (const call of calls) {
    const j = byId.get(call.id);
    const verdicts = [];
    promotedTotal += call.claims.filter((c) => c.promoted).length;
    if (j) {
      judgedCalls += 1;
      for (const v of j.verdicts || []) {
        const i = Math.floor(Number(v.claim));
        if (!(i >= 0 && i < call.claims.length)) continue;
        const verdict = v.verdict === "accept" ? "accept" : "reject";
        verdicts.push({ claim: i, verdict, reason: clip(v.reason || "", 160) });
        if (call.claims[i].promoted) {
          if (verdict === "accept") promotedConfirmed += 1;
          else promotedRefuted += 1;
        } else if (verdict === "accept") accepted += 1;
        else rejected += 1;
      }
    }
    const missing = [];
    for (const m of j?.missing || []) {
      if (quoteVerifies(m.quote, call)) {
        missing.push({ kind: clip(m.kind, 40), subject: clip(m.subject, 240), quote: clip(m.quote, 200) });
        verified += 1;
      } else {
        discarded += 1;
      }
    }
    out.push({
      id: call.id,
      turnIndex: call.turnIndex,
      blockIndex: call.blockIndex,
      name: call.name,
      shape: call.shape,
      claims: call.claims,
      verdicts,
      missing,
      judged: Boolean(j),
    });
  }
  return {
    calls: out,
    totals: {
      claims: calls.reduce((n, c) => n + c.claims.filter((x) => !x.promoted).length, 0),
      accepted,
      rejected,
      verified,
      discarded,
      ...(promotedTotal ? { promoted: { total: promotedTotal, confirmed: promotedConfirmed, refuted: promotedRefuted } } : {}),
    },
    coverage: { judged: judgedCalls, total: calls.length },
  };
}

// ── verified additions as structured ops ────────────────────────────────────
// A verified addition is a READING, not a pin: "file-read /etc/app.conf"
// belongs on the files tree next to the drafted reads, distinguishable but
// structured. The judge's free-form `kind` plus the subject's own shape
// decide the domain; whatever fits no known domain still lands in a
// "verified" group rather than evaporating into a glyph. Every op carries
// `verified: true` (rendered as its own tier, excluded from coverage) and the
// anchoring quote for the dock.

const PATHISH_RE = /^(?:~?\/|[A-Za-z]:\\|\.{1,2}\/)/;
const WRITE_KIND_RE = /(write|edit|append|create|delete|remove|touch|chmod|chown|move|rename|copy|mkdir|install)/;
const URL_IN_TEXT_RE = /https?:\/\/[^\s"'<>)\]}]+/;
const PATH_IN_TEXT_RE = /(?:^|[\s"'=(])((?:~?\/|[A-Za-z]:\\)[^\s"'<>)\]}:]+)/;

// A fetch against the operator's own machine or private network is a
// LIVE-SERVICE PROBE — hitting a dev Keycloak, a local ClickHouse, the app
// under test — not web research; calling it "research" misfiles the most
// operationally interesting reads a trace holds. These lane per service
// (host:port) on the services domain instead.
const PRIVATE_HOST_RE = /^(localhost|127\.|0\.0\.0\.0|\[::1\]|10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)|(\.(localhost|local|internal|svc|ts\.net))$/;
// A bare relative path: word-ish segments joined by slashes, no spaces.
const REL_PATH_RE = /^[\w.-]+(?:\/[\w.-]+)+$/;

function privateServiceOf(url) {
  try {
    const u = new URL(url);
    if (!PRIVATE_HOST_RE.test(u.hostname) && !PRIVATE_HOST_RE.test(u.host)) return null;
    return u.port ? `${u.hostname}:${u.port}` : u.hostname;
  } catch {
    return null;
  }
}

function additionOp(kind, subject, quote) {
  const k = String(kind || "").toLowerCase();
  const s = String(subject || "");
  const q = String(quote || "");
  if ((/(file|dir|path)/.test(k) && PATHISH_RE.test(s)) || (PATHISH_RE.test(s) && !/\s/.test(s))) {
    return {
      domain: "files",
      op: { kind: WRITE_KIND_RE.test(k) ? "write" : "read", path: s, plus: 0, minus: 0, readLines: 0 },
    };
  }
  // The subject SHOULD be a literal target, but a judge sometimes describes
  // instead — and the quote it anchored the item to holds the literal bytes.
  // Salvage the target from the quote before giving up on a domain.
  const forge = parseForgePath(s) || parseForgePath(q);
  if (forge) {
    const write = WRITE_KIND_RE.test(k) || /(post|patch|put|comment|merge)/.test(k);
    return {
      domain: "forge",
      op: { kind: write ? "write" : forge.num ? "read" : "browse", res: forge.res, num: forge.num, repo: forge.repo, endpoint: forge.endpoint, action: k },
    };
  }
  if (/(query|sql|clickhouse|analytics|database)/.test(k)) {
    return { domain: "query", op: { kind: "query", engine: "verified", target: s.slice(0, 80), query: s } };
  }
  const url = /^https?:\/\//.test(s) ? s : (URL_IN_TEXT_RE.exec(s) || URL_IN_TEXT_RE.exec(q))?.[0];
  if (url) {
    const service = privateServiceOf(url);
    if (service) {
      return { domain: "services", op: { kind: k || "api-call", name: service, subject: url } };
    }
    return { domain: "research", op: { kind: "fetch", url } };
  }
  // "search" from the judge is not automatically WEB search: a grep/find over
  // the local tree is file work, and a command-shaped subject proves it.
  // Local searches fall through to the catch-all with their own kind.
  const localSearch = /^\s*(grep|rg|find|ag|ack|fd|ls)\b/.test(s) || /--include=|-maxdepth|\s-name\s/.test(s);
  if (/(search|web)/.test(k) && !/(file|code|repo|local|grep)/.test(k) && !localSearch) {
    return { domain: "research", op: { kind: "search", query: s, results: [], resultCount: 0 } };
  }
  if (/(file|read|write|edit)/.test(k)) {
    // Relative repo paths are files too — "backend/AGENTS.md" belongs on the
    // tree exactly as the tools spell it. (Forge REST paths were consumed by
    // the parseForgePath branch above; "repos/…" cannot reach here as a file.)
    const rel = REL_PATH_RE.test(s) ? s : null;
    const path = rel || PATH_IN_TEXT_RE.exec(s)?.[1] || PATH_IN_TEXT_RE.exec(q)?.[1];
    if (path) {
      return { domain: "files", op: { kind: WRITE_KIND_RE.test(k) ? "write" : "read", path, plus: 0, minus: 0, readLines: 0 } };
    }
  }
  return { domain: "verified", op: { kind: k || "op", name: k || "op", subject: s } };
}

/**
 * The report's verified additions as lane-ready items — same item shape the
 * feed emits, `verified: true` on every op. buildTraceLanes routes them onto
 * the same lanes the drafted ops live on, without touching coverage.
 */
export function readingLaneItems(report) {
  const items = [];
  const push = (call, ref, m, confirmed) => {
    const mapped = additionOp(m.kind, m.subject, m.quote);
    items.push({
      id: `reading:${call.id}#${ref}`,
      domain: mapped.domain,
      op: {
        ...mapped.op,
        key: `${call.id}#${ref}`,
        verified: true,
        ...(confirmed ? { confirmed: true } : {}),
        quote: m.quote,
        ok: true,
        // Provenance the dock renders: which call this reading came from,
        // and which judge produced it — a verified op should say how it
        // came to exist, not just that it does.
        judgeModel: report?.model || null,
        fromCall: { id: call.id, name: call.name, turn: call.turnIndex },
      },
      turnIndex: call.turnIndex,
      blockIndex: call.blockIndex,
    });
  };
  for (const call of report?.calls || []) {
    call.missing.forEach((m, i) => push(call, `v${i}`, m, false));
    // A re-read judges the prior read's additions as promoted claims; the
    // CONFIRMED ones are still model readings — twice verified now — and
    // must keep their lanes rather than vanish into the claim ledger.
    (call.claims || []).forEach((c, i) => {
      if (!c.promoted) return;
      const v = (call.verdicts || []).find((x) => x.claim === i);
      if (v?.verdict === "accept") push(call, `p${i}`, c, true);
    });
  }
  return items;
}

// A one-line label per materialized reading, keyed by the call it came from —
// the residual side of the static↔dynamic link: an unclassified call's dock
// names the structured readings harvested out of it. Labels cap at 8 but
// `total` keeps counting, so the dock never understates a large harvest.
export function readingHarvestByCall(report) {
  const out = new Map();
  for (const item of readingLaneItems(report)) {
    const id = item.op.fromCall.id;
    const op = item.op;
    const label = item.domain === "files"
      ? `${op.kind} ${op.path}`
      : item.domain === "forge"
        ? `forge ${op.res}${op.num ? ` #${op.num}` : ""}`
        : item.domain === "query"
          ? `query ${String(op.query || "").slice(0, 50)}`
          : item.domain === "research"
            ? `fetch ${op.url}`
            : `${op.kind} ${op.subject || op.name || ""}`.trim();
    const entry = out.get(id) || { labels: [], total: 0 };
    entry.total += 1;
    if (entry.labels.length < 8) entry.labels.push(label);
    out.set(id, entry);
  }
  return out;
}

/**
 * The stretches of the axis that have been READ, as turn ranges -- the scan
 * strip's `coverage`, which every scan strip already speaks.
 *
 * Read per CALL, not per batch. The judge reads calls, so a turn carrying no
 * tool call was never unread -- there was nothing there to read -- and a run of
 * consecutive judged calls covers the stretch between them however many
 * prompt-and-reply turns sit in the gap. Breaking only on an UNJUDGED call is
 * what makes the gaps mean something: those are the calls this run has not
 * answered yet.
 *
 * This is also the only thing that separates "not read yet" from "read, nothing
 * wrong": a clean call produces no mark, so without coverage it leaves the axis
 * looking untouched.
 *
 * Returns null once every call is judged -- the same rule the review strip
 * follows, because a hairline under the whole axis says nothing.
 */
export function readingCoverage(report) {
  const ranges = [];
  let run = null;
  let unjudged = 0;
  for (const call of report?.calls || []) {
    const t = call.turnIndex;
    if (!call.judged || !Number.isFinite(t)) {
      if (!call.judged) unjudged += 1;
      run = null;
      continue;
    }
    if (run) run[1] = Math.max(run[1], t);
    else ranges.push((run = [t, t]));
  }
  if (!unjudged) return null;
  return ranges;
}

/**
 * The report as the dock's rows, newest first.
 *
 * One builder for both states: the settled report, or the live partial report
 * with the arrival order passed in. The dock therefore reads ONE row shape, and
 * the finished view comes from the authoritative report rather than the live
 * state the run happened to end on.
 */
export function readingRows(report, arrivalIds = null) {
  const out = [];
  for (const call of report?.calls || []) {
    if (!call.judged) continue;
    const verdicts = call.verdicts || [];
    // Every drafted claim, paired with what the judge said about it. The
    // schema asks the judge for a reason whenever it rejects ("why, in <=15
    // words") and finalizeReading keeps it; carrying it here is what lets a
    // reader see WHY a claim was disputed rather than only that it was.
    //
    // A claim with no verdict is not an accept: the judge simply did not rule
    // on it, and saying otherwise would credit the reading with an answer it
    // never gave.
    const claims = (call.claims || []).map((c, i) => {
      const v = verdicts.find((x) => x.claim === i) || null;
      return {
        index: i,
        domain: c.domain,
        kind: c.kind,
        subject: c.subject,
        detail: c.detail || null,
        promoted: !!c.promoted,
        verdict: v ? (v.verdict === "accept" ? "accept" : "reject") : "unruled",
        reason: v?.reason || null,
      };
    });
    out.push({
      id: call.id,
      turn: call.turnIndex,
      name: call.name,
      accepted: verdicts.filter((v) => v.verdict === "accept").length,
      rejected: verdicts.filter((v) => v.verdict !== "accept").length,
      claims,
      additions: call.missing || [],
    });
  }
  // Newest first. A settled report is in turn order, so reversing it reads
  // bottom-up through the trace; a run in flight passes the order its verdicts
  // ARRIVED, because with several judges going at once turn order would keep
  // re-sorting the list under the reader.
  if (arrivalIds?.length) {
    const rank = new Map(arrivalIds.map((id, i) => [id, i]));
    out.sort((a, b) => (rank.get(a.id) ?? -1) - (rank.get(b.id) ?? -1));
  }
  return out.reverse();
}

/**
 * The report's own numbers in the dock's vocabulary. The report counts CLAIMS
 * and coverage; the dock counts what the reader watched happen. Mapped in one
 * place so the finished dock can never quietly disagree with the strip.
 */
export function readingDockTotals(report) {
  const t = report?.totals || {};
  return {
    calls: report?.coverage?.judged || 0,
    accepted: t.accepted || 0,
    rejected: t.rejected || 0,
    additions: t.verified || 0,
    discarded: t.discarded || 0,
    reused: report?.reuse?.hits || 0,
  };
}

/**
 * The calls the judge actually read, for annotating residual lanes: an
 * unclassified call whose work now shows on verified lanes should SAY so
 * where it still sits, instead of reading as untouched.
 */
export function readingReadCalls(report) {
  const out = new Set();
  for (const call of report?.calls || []) {
    if (call.judged) out.add(call.id);
  }
  return out;
}

// A trace's lanes with what its reading read on them, as every reader of the
// trace builds them (its lanes view, a dock reading one of its cells, its card
// on a board): the reading's verified additions join the shell door's ops as
// lanes, and each call is marked with what it read. With no report, its lanes
// as its record and its shell door give them.
export function buildReadLanes(turns, { report = null, shellItems = null, actions = null } = {}) {
  const readingItems = report ? readingLaneItems(report) : [];
  return buildTraceLanes(turns, {
    extraItems: shellItems?.length ? [...readingItems, ...shellItems] : readingItems,
    readByModel: report ? readingReadCalls(report) : null,
    readInto: report ? readingHarvestByCall(report) : null,
    actions,
  });
}

/**
 * Marks folded to the current zoom: neighbors closer than `minPx` merge into
 * one cluster carrying the summed counts. Without this a dense reading
 * renders hundreds of overlapping glyph+count buttons whose text overflows
 * the axis container and widens the horizontal scrollport past the last turn.
 */
export function mergeReadingMarks(marks, cellW, minPx = 16) {
  const out = [];
  for (const m of marks || []) {
    const last = out[out.length - 1];
    if (last && (m.turn - last.turn) * cellW < minPx + (last.through - last.turn) * cellW) {
      last.through = m.turn;
      last.verified += m.verified;
      last.disputed += m.disputed;
      if (last.calls.length < 6) last.calls.push(...m.calls.slice(0, 6 - last.calls.length));
      continue;
    }
    out.push({ ...m, through: m.turn, calls: m.calls.slice(0, 6) });
  }
  return out;
}

/** Verified additions and disputes, per turn, for the axis strip. */
export function readingMarks(report, turnCount) {
  const byTurn = new Map();
  for (const call of report?.calls || []) {
    const t = call.turnIndex;
    if (t == null || t < 0 || t >= turnCount) continue;
    const m = byTurn.get(t) || { turn: t, verified: 0, disputed: 0, calls: [] };
    // Confirmed promoted claims stay counted as verified readings: a re-read
    // re-verified them, it did not un-happen them.
    const confirmed = (call.claims || []).filter((c, i) => c.promoted
      && (call.verdicts || []).some((v) => v.claim === i && v.verdict === "accept")).length;
    m.verified += call.missing.length + confirmed;
    m.disputed += call.verdicts.filter((v) => v.verdict === "reject").length;
    if (call.missing.length || confirmed || call.verdicts.some((v) => v.verdict === "reject")) m.calls.push(call);
    byTurn.set(t, m);
  }
  return [...byTurn.values()].filter((m) => m.verified || m.disputed).sort((a, b) => a.turn - b.turn);
}

// ── the run ──────────────────────────────────────────────────────────────────

// How often a batch may report what its judge is doing. The sub-agent hands
// events through one per token by contract; a consumer that saw all of them
// would spend the run reconciling instead of drawing.
const ACTIVITY_MS = 120;

/**
 * One batch's sub-agent events, turned into the live run's events.
 *
 * The judge's whole answer arrives as the arguments of a single forced
 * `return_result` call, and those arguments stream. Scanning them for complete
 * `calls[]` entries is what makes the reading watchable call by call — but a
 * streamed entry must pass the SAME two gates the settled pass applies, or the
 * live view would show a verdict the finished report drops:
 *   * the lazy-judge gate — an entry that verdicts none of a claimed call's
 *     claims is a non-answer, not a judgment;
 *   * the verbatim-quote gate — an addition without surviving bytes is
 *     discarded and counted, never rendered.
 */
function streamListener({ chunk, index, emit }) {
  const byId = new Map(chunk.map((c) => [c.id, c]));
  const scanner = createEntryScanner();
  let emitted = 0;
  let tail = "";
  let lastAt = 0;

  const activity = (label, delta) => {
    if (delta) tail = (tail + delta).slice(-240);
    const now = Date.now();
    if (now - lastAt < ACTIVITY_MS) return;
    lastAt = now;
    emit({ t: "activity", index, activity: label, preview: tail });
  };

  const entryLanded = (entry) => {
    const call = byId.get(String(entry?.id || ""));
    if (!call) return;
    const verdicts = (entry.verdicts || []).filter((v) => Number.isFinite(Number(v?.claim)));
    if (call.claims.length && !verdicts.length) return;
    const proposed = entry.missing || [];
    const missing = proposed.filter((m) => quoteVerifies(m?.quote, call));
    emitted += 1;
    emit({
      t: "call",
      index,
      id: call.id,
      name: call.name,
      turn: call.turnIndex,
      verdicts,
      missing,
      discarded: proposed.length - missing.length,
    });
  };

  return (event) => {
    if (event?.type === "turn_start") {
      activity("reading…");
      return;
    }
    const inner = event?.type === "message_update" ? event.assistantMessageEvent : null;
    if (!inner) return;
    switch (inner.type) {
      case "thinking_delta":
        activity("reasoning…", inner.delta);
        return;
      case "text_delta":
        activity("writing…", inner.delta);
        return;
      case "toolcall_start":
        // A forced-output child that missed the call gets re-prompted, and its
        // second attempt writes a whole new document. Anything the first
        // attempt streamed stops being this batch's answer.
        if (emitted) emit({ t: "batchreset", index });
        emitted = 0;
        tail = "";
        scanner.reset();
        activity("returning the reading…");
        return;
      case "toolcall_delta":
        // Guarded on its own: a scanner that trips on one malformed entry must
        // not stop the rest of the batch from being watched.
        try {
          for (const entry of scanner.push(inner.delta)) entryLanded(entry);
        } catch { /* the stream is an accelerator, not the answer */ }
        return;
      default:
        return;
    }
  };
}

export async function runTraceReading({ turns, drafted = null, creds = null, model = null, signal, onProgress = null, onLive = null, ignoreLedger = false }) {
  // The launching surface usually drafted already (the cache key needs the
  // claims fingerprint before any click) — don't build the feed twice.
  const { calls } = drafted || draftReadingCalls(turns);
  if (!calls.length) return { status: "error", error: new Error("this trace has no readable tool calls") };

  // Every live emit goes through here. Streaming is an ACCELERATOR, never a
  // requirement: a listener that throws is a bug in the surface watching the
  // run, and the reading is what the operator paid for. The emits sit outside
  // the per-batch try/catch by necessity (they bracket it), so without this
  // guard a throwing watcher failed the chunk — and a watcher that throws once
  // throws for every chunk, which failed the whole run with the watcher's own
  // error in place of a reading.
  const emitLive = (ev) => {
    if (!onLive) return;
    try {
      onLive(ev);
    } catch {
      /* the watcher's problem, not the run's */
    }
  };

  // Ledger hits are free; only misses spend. A banked entry with zero
  // verdicts on a call that HAS claims is a lazy judge's non-answer, not a
  // judgment — reusing it re-zeroes every later run, so it re-judges instead.
  // `ignoreLedger` is the explicit re-run: a clean-context second pass that
  // re-judges everything and re-banks, so judge drift is measurable at all.
  const hits = [];
  const misses = [];
  for (const call of calls) {
    const entry = ignoreLedger ? null : readLedgerEntry(call, model);
    const usable = entry && (!call.claims.length || (entry.verdicts || []).length > 0);
    if (usable) hits.push({ id: call.id, verdicts: entry.verdicts, missing: entry.missing, turnIndex: call.turnIndex });
    else misses.push(call);
  }

  let judged = [...hits];
  let judgeFailures = null;
  // Banked calls are answered before the run starts, at no cost — the live
  // surfaces say so at t=0 rather than letting them look freshly read.
  emitLive({
    t: "reused",
    count: hits.length,
    turns: hits.map((h) => h.turnIndex).filter((t) => Number.isFinite(t)),
  });
  if (misses.length) {
    const sys = readingSystemPrompt();
    const contextWindow = lookupCatalogModel(creds?.provider, model)?.contextWindow || null;
    const budget = windowBudgetChars({ contextWindow, prefixChars: sys.length + 500 });
    const chunks = packReadingChunks(misses, budget);
    let done = 0;
    onProgress?.({ done: 0, total: chunks.length, reused: hits.length });
    const tasks = chunks.map((chunk, index) => async () => {
      // A chunk is a contiguous run of calls (packReadingChunks preserves
      // order), so it occupies a contiguous stretch of the axis: the batch IS
      // a reading head sweeping a turn range, and READ_CONCURRENCY of them
      // sweep at once. Handing the range out is also the honest picture of
      // what this judge could see together — no batch shares context with any
      // other.
      const turnsOf = chunk.map((c) => c.turnIndex).filter((t) => Number.isFinite(t));
      emitLive({
        t: "batch",
        index,
        from: turnsOf.length ? Math.min(...turnsOf) : 0,
        to: turnsOf.length ? Math.max(...turnsOf) : 0,
        size: chunk.length,
      });
      let r;
      try {
        r = await runSubAgent({
          role: "primary",
          model: model || null,
          signal,
          tools: [],
          outputSchema: READING_REPORT_SCHEMA,
          systemPrompt: sys,
          onEvent: onLive ? streamListener({ chunk, index, emit: emitLive }) : undefined,
          task: [
            `Audit the reading of ${chunk.length} tool call${chunk.length === 1 ? "" : "s"}. Return one entry per call id.`,
            "",
            ...chunk.map(callCard),
          ].join("\n"),
        });
      } catch (e) {
        r = { status: "error", error: e instanceof Error ? e : new Error(String(e)) };
      }
      done += 1;
      onProgress?.({ done, total: chunks.length, reused: hits.length });
      emitLive({ t: "batchdone", index, status: r.status === "done" ? "done" : "error" });
      return { chunk, status: r.status, raw: r.status === "done" ? r.result : null, error: r.error || null };
    });
    const settled = await runWithConcurrency(tasks, READ_CONCURRENCY);
    if (signal?.aborted) return { status: "aborted" };
    const passes = settled.map((s, i) => (s.ok ? s.value : { chunk: chunks[i], status: "error", raw: null, error: s.error }));
    if (!passes.some((p) => p.status === "done" && p.raw)) {
      return { status: "error", error: passes.find((p) => p.error)?.error || new Error("the reading returned no report") };
    }
    const fresh = [];
    for (const p of passes) {
      if (p.status !== "done" || !Array.isArray(p.raw?.calls)) continue;
      const inChunk = new Map(p.chunk.map((c) => [c.id, c]));
      for (const entry of p.raw.calls) {
        const call = inChunk.get(String(entry?.id || ""));
        if (!call) continue;
        const verdicts = (entry.verdicts || []).filter((v) => Number.isFinite(Number(v?.claim)));
        // Same lazy-judge gate as the ledger read: an entry that verdicts
        // none of a claimed call's claims is a non-answer. Banking it would
        // freeze the zeros into every future run.
        if (call.claims.length && !verdicts.length) continue;
        const missing = (entry.missing || []).filter((m) => quoteVerifies(m?.quote, call));
        fresh.push({ id: call.id, verdicts, missing, call });
      }
    }
    // A judge failure must never masquerade as a clean zero-count reading:
    // when nothing usable came back the run FAILED, and a partial return is
    // reported on the strip so the totals read as partial, not as verdicts.
    if (!fresh.length) {
      return {
        status: "error",
        error: passes.find((p) => p.error)?.error || new Error("the judge returned no usable verdicts — nothing was judged this run"),
      };
    }
    const freshIds = new Set(fresh.map((f) => f.id));
    const unjudged = misses.filter((c) => !freshIds.has(c.id)).length;
    if (unjudged) {
      const cause = passes.find((p) => p.error)?.error;
      judgeFailures = {
        calls: unjudged,
        // A batch counts as failed when any of its calls went unjudged —
        // whole-chunk errors AND "successful" chunks whose entries were
        // dropped by the lazy-judge gate or absent from the response.
        // Counting only hard errors rendered "failed on 0 batches" right
        // beside a nonzero unjudged count.
        chunks: passes.filter((p) => p.chunk.some((c) => !freshIds.has(c.id))).length,
        message: String(cause?.message || cause || "the judge skipped these calls or returned unusable entries").slice(0, 200),
      };
    }
    // A benchmark pass is always full-cost by definition and judges MERGED
    // claim sets whose hashes no normal run will ever ask for — banking them
    // would only crowd real entries out of the capped ledger.
    if (!ignoreLedger) {
      writeLedgerEntries(fresh.map(({ call, verdicts, missing }) => ({ call, verdicts, missing })), model);
    }
    judged = judged.concat(fresh.map(({ id, verdicts, missing }) => ({ id, verdicts, missing })));
  }

  // The reuse split is the run's own price tag: a re-run on an unchanged
  // drafter fleet is ALL hits — correct and free, but indistinguishable from
  // a dead button unless the strip says so.
  return {
    status: "done",
    report: {
      ...finalizeReading(calls, judged),
      model: model || null,
      reuse: { hits: hits.length, judged: misses.length },
      ...(judgeFailures ? { judgeFailures } : {}),
    },
  };
}

// ── reading history: the longitudinal benchmark ──────────────────────────────
// A re-run of the SAME pass over the same trace measures whether the drafter
// fleet improved: additions migrating into drafted claims, disputes falling,
// acceptance rising. Each completed run snapshots its totals per trace;
// the delta between the latest two snapshots is the flywheel's own progress
// bar, rendered on the strip. The ledger already prices the re-run honestly —
// unchanged calls cache-hit, so cost is proportional to drafter churn.

const HISTORY_KEY = "origin.trace-reading.history.v1";
const HISTORY_TRACES_MAX = 120;
const HISTORY_SNAPSHOTS_MAX = 8;

function readHistoryRaw() {
  try {
    const raw = window.localStorage?.getItem(HISTORY_KEY);
    const v = raw ? JSON.parse(raw) : null;
    return v && typeof v === "object" && !Array.isArray(v) ? v : {};
  } catch {
    return {};
  }
}

function writeHistoryRaw(history) {
  try {
    const ids = Object.keys(history);
    if (ids.length > HISTORY_TRACES_MAX) {
      ids.sort((a, b) => (history[a].at(-1)?.at || 0) - (history[b].at(-1)?.at || 0));
      for (const id of ids.slice(0, ids.length - HISTORY_TRACES_MAX)) delete history[id];
    }
    window.localStorage?.setItem(HISTORY_KEY, JSON.stringify(history));
  } catch {
    /* private mode / quota — history is a lens, not a requirement */
  }
}

/** The most recent snapshot for a trace, or null. */
export function readReadingSnapshot(traceId) {
  const runs = readHistoryRaw()[String(traceId || "")];
  return runs?.length ? runs[runs.length - 1] : null;
}

/**
 * Record a completed run. A run identical to the last snapshot (same claims
 * fingerprint, judge, and prompt — nothing changed, nothing re-judged) only
 * refreshes its timestamp instead of consuming a history slot.
 */
export function writeReadingSnapshot(traceId, { claimsHash, model, report }) {
  if (!traceId || !report) return;
  const history = readHistoryRaw();
  const runs = history[String(traceId)] || [];
  const snap = {
    at: Date.now(),
    claimsHash: claimsHash || "",
    model: model || "",
    promptVersion: PROMPT_VERSION,
    totals: report.totals,
    coverage: report.coverage,
  };
  const last = runs[runs.length - 1];
  if (last && last.claimsHash === snap.claimsHash && last.model === snap.model && last.promptVersion === snap.promptVersion) {
    runs[runs.length - 1] = snap;
  } else {
    runs.push(snap);
    if (runs.length > HISTORY_SNAPSHOTS_MAX) runs.splice(0, runs.length - HISTORY_SNAPSHOTS_MAX);
  }
  history[String(traceId)] = runs;
  writeHistoryRaw(history);
}

/**
 * The delta between the trace's LAST snapshot and a current report — computed
 * before the current run snapshots, so it always compares against the prior
 * reading. Null when there is no prior run or nothing changed.
 */
export function readingDelta(traceId, report) {
  const prior = readReadingSnapshot(traceId);
  if (!prior?.totals || !report?.totals) return null;
  const d = {
    at: prior.at,
    claims: report.totals.claims - prior.totals.claims,
    accepted: report.totals.accepted - prior.totals.accepted,
    verified: report.totals.verified - prior.totals.verified,
    disputed: report.totals.rejected - prior.totals.rejected,
    prior: { claims: prior.totals.claims, verified: prior.totals.verified, rejected: prior.totals.rejected },
  };
  return d.claims || d.verified || d.disputed || d.accepted ? d : null;
}

/** Drop a trace's in-memory report so the run button re-executes the pass. */
export function clearReadingCache(key) {
  forgetReading(key);
}
